import React, { useState, useEffect } from 'react';
import { AuthorStatusBadge } from '../../utils/authorStatus';
import { Link } from 'react-router-dom';
import NotesFilter from './NotesFilter';
import { Note } from '../../types/interfaces';

// Define FilterOptions interface locally since it's not exported from NotesFilter
interface FilterOptions {
  status?: string;
  category?: string;
  tag?: string;
  location?: string;
  sortBy?: string;
  search?: string;
  page?: number;
  limit?: number;
}

interface NotesListProps {
  initialNotes?: Note[];
  isLoading?: boolean;
  error?: string;
  showFilters?: boolean;
  showStatusFilter?: boolean;
  /** Moderator/admin view: show raw moderation statuses. Otherwise only Approved/Pending. */
  showRawStatus?: boolean;
  onFilterChange?: (filters: FilterOptions) => void;
  emptyMessage?: string;
  showActions?: boolean;
  onHeartClick?: (noteId: string) => void;
  userHeartedNotes?: string[];
  isUserNotes?: boolean;
  onNoteClick?: (noteId: string) => void;
  searchTerm?: string;
  onLoadMore?: () => void;
  hasMorePages?: boolean;
  isLoadingMore?: boolean;
  totalPages?: number;
  currentPage?: number;
}

const NotesList: React.FC<NotesListProps> = ({
  initialNotes = [],
  isLoading = false,
  error = '',
  showFilters = true,
  showStatusFilter = false,
  showRawStatus = false,
  onFilterChange,
  emptyMessage = 'No notes found.',
  showActions = true,
  onHeartClick,
  userHeartedNotes = [],
  isUserNotes = false,
  onNoteClick,
  searchTerm = '',
  onLoadMore,
  hasMorePages = false,
  isLoadingMore = false,
  totalPages,
  currentPage = 1,
}) => {
  const [localNotes, setLocalNotes] = useState<Note[]>(initialNotes || []);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    status: '',
    category: '',
    tag: '',
    location: '',
    sortBy: 'newest',
    search: searchTerm || '',
  });
  
  // Update local notes when initialNotes changes
  useEffect(() => {
    setLocalNotes(initialNotes);
  }, [initialNotes]);
  
  // Handle filter changes
  const handleFilterChange = (newFilters: FilterOptions) => {
    setFilterOptions(newFilters);
    
    if (onFilterChange) {
      onFilterChange(newFilters);
    } else {
      // If no external filter handler, filter locally (for testing/demo)
      filterNotesLocally(newFilters);
    }
  };
  
  // Local filtering function (for demo/testing without API)
  const filterNotesLocally = (filters: FilterOptions) => {
    let filtered = [...initialNotes];
    
    // Filter by status
    if (filters.status) {
      filtered = filtered.filter(note => note.moderation?.status === filters.status);
    }
    
    // Filter by search term
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        note => 
          note.note.toLowerCase().includes(searchLower) || 
          (note.contributorAliasName && note.contributorAliasName.toLowerCase().includes(searchLower))
      );
    }
    
    // Filter by category
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(
        note => note.categories && note.categories.includes(filters.category || '')
      );
    }
    
    // Filter by tag
    if (filters.tag && filters.tag !== 'all') {
      filtered = filtered.filter(
        note => note.tags && note.tags.includes(filters.tag || '')
      );
    }
    
    // Filter by location
    if (filters.location && filters.location !== 'all') {
      filtered = filtered.filter(
        note => 
          (note.location_city && note.location_city.includes(filters.location || '')) ||
          (note.location_state && note.location_state.includes(filters.location || ''))
      );
    }
    
    // Sort notes
    if (filters.sortBy) {
      // Handle "random" sort separately - sorts by _id for consistent but mixed ordering
      if (filters.sortBy === 'random') {
        filtered.sort((a, b) => {
          // Sort by _id ascending for a consistent "mixed" order
          return (a._id || '').localeCompare(b._id || '');
        });
      } else {
        const [field, direction] = filters.sortBy.split('_');

        filtered.sort((a, b) => {
          let aValue: number, bValue: number;

          // Determine the sort field
          switch (field) {
            case 'createdAt':
              aValue = new Date(a.createdAt).getTime();
              bValue = new Date(b.createdAt).getTime();
              break;
            case 'heart_count':
              aValue = a.heart_count;
              bValue = b.heart_count;
              break;
            case 'viewCount':
              aValue = a.display?.viewCount || 0;
              bValue = b.display?.viewCount || 0;
              break;
            default:
              aValue = new Date(a.createdAt).getTime();
              bValue = new Date(b.createdAt).getTime();
          }

          // Apply sort direction
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        });
      }
    }
    
    setLocalNotes(filtered);
  };
  
  // Handle load more button click
  const handleLoadMore = () => {
    if (onLoadMore && hasMorePages && !isLoadingMore) {
      onLoadMore();
    }
  };
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };
  
  // Truncate note content for preview
  const truncateNote = (content: string, maxLength = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };
  
  // Render note status badge
  const renderStatusBadge = (status?: string) => {
    // Don't show status badge if user doesn't have permission to see moderation data
    if (!status || status === 'Unknown') {
      return null; // Hide status badge for users without moderation permissions
    }

    // Outside the moderator view (e.g. an author's own notes) show Approved or Pending only
    if (!showRawStatus) {
      return <AuthorStatusBadge status={status} />;
    }

    let badgeClass = 'bg-secondary';
    let label = 'Unknown';
    
    switch (status) {
      case 'pending':
        badgeClass = 'bg-warning text-dark';
        label = 'Pending';
        break;
      case 'ai-approved':
        badgeClass = 'bg-info text-dark';
        label = 'AI Approved';
        break;
      case 'moderator-approved':
        badgeClass = 'bg-success';
        label = 'Approved';
        break;
      case 'rejected':
        badgeClass = 'bg-danger';
        label = 'Rejected';
        break;
    }
    
    return <span className={`badge ${badgeClass}`}>{label}</span>;
  };
  
  // Function to highlight search term in text
  const highlightSearchTerm = (text: string, searchTerm?: string) => {
    if (!searchTerm || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    if (parts.length === 1) return text;
    
    return (
      <>
        {parts.map((part, i) => 
          regex.test(part) ? <mark key={i} className="bg-warning">{part}</mark> : part
        )}
      </>
    );
  };
  
  // Render the notes list
  return (
    <div className="notes-list-container">
      {/* Filters */}
      {showFilters && (
        <NotesFilter
          filters={filterOptions}
          onChange={handleFilterChange}
          showStatusFilter={showStatusFilter}
        />
      )}
      
      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      {/* Notes List */}
      {isLoading ? (
        <div className="text-center my-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading notes...</p>
        </div>
      ) : localNotes.length > 0 ? (
        <>
          <div className="row">
            {localNotes.map(note => (
              <div key={note._id} className="col-12 col-md-6 col-lg-4 mb-4">
                <div
                  className={`card h-100 ${onNoteClick ? 'card-clickable' : ''}`}
                  onClick={() => onNoteClick && onNoteClick(note.publicId)}
                >
                  <div className="card-body">
                    {/* Status Badge */}
                    <div className="d-flex justify-content-between mb-2">
                      {renderStatusBadge(note.moderation?.status)}
                      <small className="text-muted">{formatDate(note.createdAt)}</small>
                    </div>
                    
                    {/* Note Content */}
                    <h5 className="card-title">
                      {highlightSearchTerm(note.contributorAliasName || 'Anonymous', filterOptions.search)}
                    </h5>
                    <p className="card-text">
                      {highlightSearchTerm(truncateNote(note.note), filterOptions.search)}
                    </p>
                    
                    {/* Location */}
                    {(note.location_city || note.location_state) && (
                      <div className="mb-2 text-muted">
                        <small>
                          <i className="fas fa-map-marker-alt me-2"></i>
                          {highlightSearchTerm(note.location_city || '', filterOptions.search)}{note.location_city && note.location_state && ', '}
                          {highlightSearchTerm(note.location_state || '', filterOptions.search)}
                        </small>
                      </div>
                    )}
                    
                    {/* Tags & Categories (if available) */}
                    {note.categories && note.categories.length > 0 && (
                      <div className="mb-2">
                        {note.categories.map((category, index) => (
                          <span key={index} className="badge bg-primary me-1 mb-1">
                            {highlightSearchTerm(category, filterOptions.search)}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {note.tags && note.tags.length > 0 && (
                      <div className="mb-2">
                        {note.tags.map((tag, index) => (
                          <span key={index} className="badge bg-secondary me-1 mb-1">
                            {highlightSearchTerm(tag, filterOptions.search)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Card Footer with Stats & Actions */}
                  <div className="card-footer d-flex justify-content-between align-items-center">
                    <small className="text-muted">
                      Read More <i className="fas fa-arrow-right"></i>
                    </small>
                    {/* Only show heart count if greater than 0 */}
                    {note.heart_count > 0 && (
                      <small className="text-danger">
                        <i className="fas fa-heart me-1"></i>
                        {note.heart_count}
                      </small>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Load More Button */}
          {hasMorePages && (
            <div className="text-center my-4">
              {totalPages && currentPage && (
                <p className="text-muted mb-2">
                  Page {currentPage} of {totalPages}
                </p>
              )}
              <button 
                className="btn btn-outline-primary" 
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Loading More Notes...
                  </>
                ) : (
                  'Load More Notes'
                )}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center my-5">
          <p className="text-muted">{emptyMessage}</p>
          {isUserNotes && (
            <Link to="/submit" className="btn btn-primary mt-3">
              Submit Your First Note
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default NotesList; 